import { PrintJobStatus, type KioskSessionView } from '@print-saas/shared';
import { ProgressBar } from '../components/ProgressBar';
import { plural } from '../lib/format';

export function PrintingScreen({ session }: { session: KioskSessionView }) {
  const job = session.job;
  const started = job !== null && job.status !== PrintJobStatus.QUEUED;
  const total = job?.filesTotal || session.fileCount;
  const done = Math.min(job?.filesDone ?? 0, total);
  const progress = started && total > 0 ? done / total : null;
  const current = Math.min(done + 1, total);

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-[4vmin] px-[6vmin] text-center">
      <PrinterAnimation />
      <div>
        <p className="text-[2.8vmin] font-semibold tracking-[0.2em] text-go uppercase">
          Payment received ✓
        </p>
        <h1 className="mt-2 text-[8vmin] leading-[1.05] font-extrabold tracking-tight">
          {started ? 'Printing…' : 'Sending to printer…'}
        </h1>
      </div>
      <div className="w-full max-w-[100vmin]">
        <ProgressBar value={progress} />
        <div className="mt-[2vmin] flex justify-between text-[3vmin] text-muted tabular-nums">
          <span>
            {started && total > 0 ? (
              <>
                File <b className="text-text">{current}</b> of <b className="text-text">{total}</b>
              </>
            ) : (
              plural(total, 'file')
            )}
          </span>
          <span>{plural(session.totalSheets, 'sheet')}</span>
        </div>
      </div>
      <p className="text-[3vmin] text-muted">Please wait here — your prints will come out below.</p>
    </div>
  );
}

/** Stylised printer with a sheet sliding out (pure CSS animation). */
function PrinterAnimation() {
  return (
    <div className="relative h-[24vmin] w-[30vmin]" aria-hidden="true">
      <div className="absolute inset-x-[3vmin] top-0 h-[9vmin] rounded-t-[1.5vmin] bg-line" />
      <div className="absolute inset-x-0 top-[7vmin] z-10 h-[10vmin] rounded-[2vmin] bg-panel ring-2 ring-line">
        <span className="absolute top-[2.5vmin] right-[3vmin] size-[1.6vmin] animate-pulse-dot rounded-full bg-go" />
        <span className="absolute inset-x-[5vmin] bottom-[2vmin] h-[1vmin] rounded-full bg-ink" />
      </div>
      <div className="absolute inset-x-[7vmin] top-[15vmin] h-[9vmin] overflow-hidden">
        <div className="animate-paper h-full rounded-b-[0.8vmin] bg-white shadow-lg">
          <div className="mx-[2vmin] space-y-[0.9vmin] pt-[2vmin]">
            <div className="h-[0.7vmin] rounded bg-slate-300" />
            <div className="h-[0.7vmin] w-3/4 rounded bg-slate-300" />
            <div className="h-[0.7vmin] w-5/6 rounded bg-slate-300" />
          </div>
        </div>
      </div>
    </div>
  );
}
